import React, { useState, useEffect } from 'react';
import { Box, Button, TextField } from '@mui/material';
import {
  BorderColor as BulkEditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FilterDrawer from './FilterDrawer';
import SelectField from '../inputs/SelectField';

export const DEFAULT_LOTS_FILTERS = {
  shape: '',
  availableOnly: true,
  minCaratPerStone: '',
  maxCaratPerStone: '',
  minLength: '',
  maxLength: '',
  minWidth: '',
  maxWidth: '',
};

/**
 * Filter drawer for non-certified lots. Keeps draft filter state local so typing
 * does not re-render the parent inventory page.
 */
export default function LotsFilterDrawer({
  open,
  onClose,
  appliedFilters,
  shapeList,
  onApply,
  onClearAll,
  isManager = false,
  onBulkEdit,
  includeAllShapeOption = true,
  defaultShape = '',
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(DEFAULT_LOTS_FILTERS);

  useEffect(() => {
    if (!open) return;
    setDraft(appliedFilters);
    // Sync draft only when the drawer opens; appliedFilters is read at open time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleClear = () => {
    const cleared = includeAllShapeOption
      ? DEFAULT_LOTS_FILTERS
      : { ...DEFAULT_LOTS_FILTERS, shape: defaultShape };
    setDraft(cleared);
    onClearAll();
  };

  return (
    <FilterDrawer
      open={open}
      onClose={onClose}
      title={t('inventory.filters')}
      actions={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button fullWidth variant="contained" onClick={handleApply} sx={{ flex: 1, minWidth: 0 }}>
              {t('common.save')}
            </Button>
            {isManager && onBulkEdit && (
              <Button
                type="button"
                variant="outlined"
                size="medium"
                startIcon={<BulkEditIcon />}
                onClick={() => {
                  onClose();
                  onBulkEdit();
                }}
                sx={{ flex: 1, minWidth: 0 }}
              >
                {t('inventory.bulkEdit')}
              </Button>
            )}
          </Box>
          <Button fullWidth variant="outlined" color="inherit" onClick={handleClear}>
            {t('inventory.clearFilters')}
          </Button>
        </Box>
      }
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <SelectField
          label="Shape"
          fullWidth
          value={draft.shape}
          onChange={(next) => setDraft((prev) => ({ ...prev, shape: next }))}
          options={shapeList.map((s) => {
            const shape = String(s).toUpperCase();
            return { value: shape, label: shape };
          })}
          emptyLabel="All"
          includeEmptyOption={includeAllShapeOption}
        />
        <SelectField
          label="Show"
          fullWidth
          value={draft.availableOnly ? 'available' : 'all'}
          onChange={(next) => setDraft((prev) => ({ ...prev, availableOnly: next === 'available' }))}
          options={[
            { value: 'all', label: 'All lots' },
            { value: 'available', label: 'Available only' },
          ]}
          emptyLabel=""
        />
        <TextField size="small" label="Min ct/stone" type="number" value={draft.minCaratPerStone} onChange={(e) => setDraft((prev) => ({ ...prev, minCaratPerStone: e.target.value }))} inputProps={{ step: 0.01, min: 0 }} fullWidth />
        <TextField size="small" label="Max ct/stone" type="number" value={draft.maxCaratPerStone} onChange={(e) => setDraft((prev) => ({ ...prev, maxCaratPerStone: e.target.value }))} inputProps={{ step: 0.01, min: 0 }} fullWidth />
        <TextField size="small" label="Min length (mm)" type="number" value={draft.minLength} onChange={(e) => setDraft((prev) => ({ ...prev, minLength: e.target.value }))} inputProps={{ step: 0.1, min: 0 }} fullWidth />
        <TextField size="small" label="Max length (mm)" type="number" value={draft.maxLength} onChange={(e) => setDraft((prev) => ({ ...prev, maxLength: e.target.value }))} inputProps={{ step: 0.1, min: 0 }} fullWidth />
        <TextField size="small" label="Min width (mm)" type="number" value={draft.minWidth} onChange={(e) => setDraft((prev) => ({ ...prev, minWidth: e.target.value }))} inputProps={{ step: 0.1, min: 0 }} fullWidth />
        <TextField size="small" label="Max width (mm)" type="number" value={draft.maxWidth} onChange={(e) => setDraft((prev) => ({ ...prev, maxWidth: e.target.value }))} fullWidth />
      </Box>
    </FilterDrawer>
  );
}
